// ============================================================================
// Relevance matching between sector landing pages and HR help articles.
//
// Scoring blends two signals:
//   1. Keyword overlap  - shared terms between the sector's keyword cluster
//                         (keywords, primary keyword, challenges, FAQs,
//                         section headings) and the article's own keyword
//                         surface (keywords, title, excerpt, category,
//                         takeaways).
//   2. User intent      - each article is classified into an intent bucket
//                         (compliance, disputes, pay, people, change) and each
//                         sector has a ranked list of intents its employers
//                         actually search with. Matching intent is boosted.
// Deterministic: no randomness, so SSR and client render identically.
// ============================================================================
import { blogPosts, type BlogPost } from "@/content/blog";
import type { Industry } from "@/content/industries";

export type ArticleIntent = "compliance" | "disputes" | "pay" | "people" | "change";

export const INTENT_LABEL: Record<ArticleIntent, string> = {
  compliance: "Process & compliance",
  disputes: "Dispute & tribunal risk",
  pay: "Pay & entitlements",
  people: "People & culture",
  change: "Restructuring & change",
};

/** Terms that signal each intent, used to classify an article's surface text. */
const INTENT_SIGNALS: Record<ArticleIntent, string[]> = {
  compliance: [
    "process", "procedure", "checklist", "policy", "compliance", "statutory",
    "disciplinary", "grievance", "record", "documentation", "duty", "legislation",
  ],
  disputes: [
    "tribunal", "claim", "acas", "conciliation", "dismissal", "unfair",
    "liability", "settlement", "litigation", "dispute", "harassment",
  ],
  pay: [
    "pay", "wage", "salary", "holiday", "entitlement", "sick", "ssp", "smp",
    "redundancy pay", "notice pay", "minimum wage", "furlough", "pension",
  ],
  people: [
    "flexible", "hybrid", "culture", "wellbeing", "retention", "engagement",
    "onboarding", "recruitment", "training", "harassment", "parental", "leave",
  ],
  change: [
    "redundancy", "restructure", "tupe", "transfer", "consultation",
    "closure", "reorganisation", "headcount",
  ],
};

/** Ranked intents per sector. Earlier entries carry a bigger boost. */
const SECTOR_INTENTS: Record<string, ArticleIntent[]> = {
  hospitality: ["pay", "people", "compliance"],
  healthcare: ["compliance", "disputes", "pay"],
  "care-homes": ["compliance", "pay", "people"],
  "charities-non-profits": ["change", "people", "pay"],
  construction: ["change", "compliance", "disputes"],
  "retail-ecommerce": ["change", "pay", "people"],
  manufacturing: ["change", "disputes", "compliance"],
  "professional-services": ["people", "disputes", "compliance"],
};

const DEFAULT_INTENTS: ArticleIntent[] = ["compliance", "change", "pay"];

const STOP_WORDS = new Set([
  "the", "and", "for", "with", "your", "you", "our", "that", "this", "from",
  "have", "has", "are", "was", "were", "will", "what", "when", "how", "why",
  "who", "can", "should", "must", "into", "out", "about", "their", "them",
  "they", "its", "it", "a", "an", "of", "to", "in", "on", "at", "by", "or",
  "as", "is", "be", "we", "us", "uk", "hr", "employer", "employers",
  "employee", "employees", "staff", "business", "businesses", "support",
  "guide", "services", "service", "team", "teams", "work", "working",
  "does", "much", "make", "makes", "need", "needs", "take", "takes", "give",
  "gives", "get", "gets", "just", "also", "more", "most", "many", "use",
  "uses", "using", "way", "ways", "each", "every", "any", "one", "two",
  "step", "steps", "guidance", "advice", "people", "year", "years", "time",
  "times", "right", "well", "good", "same", "than", "then", "these", "those",
  "before", "after", "over", "under", "while", "where", "which", "been",
  "being", "there", "here", "own", "new", "still", "even", "like", "come",
  "comes", "know", "knows", "said", "says", "sure", "part", "point", "case",
  "cases", "thing", "things", "want", "wants", "keep", "keeps", "put",
]);


function tokenise(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, " ")
    .split(/[\s-]+/)
    .filter((t) => t.length > 2 && !STOP_WORDS.has(t));
}

/** Everything a sector page is "about", weighted by how central it is. */
function sectorSurface(industry: Industry): Map<string, number> {
  const weighted: Array<[string, number]> = [
    [industry.primaryKeyword, 3],
    [(industry.keywords ?? []).join(" "), 3],
    [industry.name, 2],
    [industry.summary, 1],
    [industry.overview, 1],
    [industry.challenges.map((c) => `${c.title} ${c.description}`).join(" "), 2],
    [(industry.sections ?? []).map((s) => s.heading).join(" "), 2],
    [industry.faqs.map((f) => f.question).join(" "), 2],
  ];

  const map = new Map<string, number>();
  for (const [text, weight] of weighted) {
    for (const token of tokenise(text ?? "")) {
      map.set(token, (map.get(token) ?? 0) + weight);
    }
  }
  return map;
}

/** Everything an article is "about". */
function articleSurface(post: BlogPost): Map<string, number> {
  const weighted: Array<[string, number]> = [
    [post.primaryKeyword ?? "", 3],
    [(post.keywords ?? []).join(" "), 3],
    [post.title, 2],
    [post.category, 2],
    [post.excerpt, 1],
    [post.keyTakeaways.join(" "), 1],
    [(post.faqs ?? []).map((f) => f.question).join(" "), 1],
  ];

  const map = new Map<string, number>();
  for (const [text, weight] of weighted) {
    for (const token of tokenise(text)) {
      map.set(token, (map.get(token) ?? 0) + weight);
    }
  }
  return map;
}

/** Multi-word keyword phrases shared between a sector and an article. */
function sharedPhrases(industry: Industry, post: BlogPost): string[] {
  const sectorText = [
    industry.primaryKeyword,
    (industry.keywords ?? []).join(" | "),
    industry.challenges.map((c) => c.title).join(" | "),
    (industry.sections ?? []).map((s) => s.heading).join(" | "),
    industry.faqs.map((f) => f.question).join(" | "),
  ]
    .join(" | ")
    .toLowerCase();

  const articleText = [
    post.primaryKeyword ?? "",
    post.title,
    post.category,
    post.excerpt,
    (post.keywords ?? []).join(" | "),
  ]
    .join(" | ")
    .toLowerCase();

  const candidates = new Set<string>();
  for (const phrase of [
    ...(post.keywords ?? []),
    post.primaryKeyword ?? "",
  ]) {
    const clean = phrase.toLowerCase().trim();
    if (clean.split(/\s+/).length >= 2 && clean.length > 6) candidates.add(clean);
  }
  for (const phrase of industry.keywords ?? []) {
    const clean = phrase.toLowerCase().trim();
    if (clean.split(/\s+/).length >= 2 && clean.length > 6) candidates.add(clean);
  }

  const hits: string[] = [];
  candidates.forEach((phrase) => {
    if (sectorText.includes(phrase) && articleText.includes(phrase)) hits.push(phrase);
  });
  return hits.sort((a, b) => b.length - a.length || a.localeCompare(b)).slice(0, 3);
}

/** Classifies an article into the intent its language leans towards. */
export function articleIntent(post: BlogPost): ArticleIntent {
  const haystack = [
    post.primaryKeyword ?? "",
    post.title,
    post.category,
    post.excerpt,
    (post.keywords ?? []).join(" "),
  ]
    .join(" ")
    .toLowerCase();

  let best: ArticleIntent = "compliance";
  let bestScore = -1;
  (Object.keys(INTENT_SIGNALS) as ArticleIntent[]).forEach((intent) => {
    const score = INTENT_SIGNALS[intent].reduce((acc, signal) => {
      const occurrences = haystack.split(signal).length - 1;
      // Longer, more specific signals count for more than single common words.
      return acc + occurrences * (signal.includes(" ") ? 3 : 1);
    }, 0);
    if (score > bestScore) {
      bestScore = score;
      best = intent;
    }
  });
  return best;
}

export interface RelatedArticle {
  post: BlogPost;
  /** 0-100 relevance, shown to no one but useful for ordering and testing. */
  score: number;
  intent: ArticleIntent;
  intentLabel: string;
  /** Shared terms that earned the match, used as descriptive context. */
  matchedTerms: string[];
  /** Human sentence explaining why this article is here. */
  reason: string;
}

export interface RelatedArticlesOptions {
  limit?: number;
  /** Slugs to exclude, e.g. articles already linked elsewhere on the page. */
  exclude?: string[];
}

/**
 * Returns the most relevant HR help articles for a sector landing page,
 * ranked by keyword overlap and sector intent.
 */
export function relatedArticlesForIndustry(
  industry: Industry,
  { limit = 4, exclude = [] }: RelatedArticlesOptions = {}
): RelatedArticle[] {
  const sector = sectorSurface(industry);
  const intents = SECTOR_INTENTS[industry.slug] ?? DEFAULT_INTENTS;
  const excluded = new Set(exclude);

  const scored = blogPosts
    .filter((post) => !excluded.has(post.slug))
    .map((post) => {
      const surface = articleSurface(post);
      let overlap = 0;
      const matched: Array<[string, number]> = [];

      surface.forEach((articleWeight, token) => {
        const sectorWeight = sector.get(token);
        if (!sectorWeight) return;
        const contribution = Math.min(articleWeight, 3) * Math.min(sectorWeight, 3);
        overlap += contribution;
        matched.push([token, contribution]);
      });

      const intent = articleIntent(post);
      const intentRank = intents.indexOf(intent);
      const intentBoost = intentRank === -1 ? 0 : (intents.length - intentRank) * 6;

      const phrases = sharedPhrases(industry, post);
      const tokenTerms = matched
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .slice(0, 3)
        .map(([token]) => token);
      const matchedTerms = phrases.length ? phrases : tokenTerms;

      return {
        post,
        rawScore: overlap + intentBoost + phrases.length * 8,
        intent,
        matchedTerms,
      };
    })
    .sort((a, b) => b.rawScore - a.rawScore || a.post.slug.localeCompare(b.post.slug));

  const top = scored.slice(0, limit);
  const max = top[0]?.rawScore || 1;

  return top.map((entry) => ({
    post: entry.post,
    score: Math.round((entry.rawScore / max) * 100),
    intent: entry.intent,
    intentLabel: INTENT_LABEL[entry.intent],
    matchedTerms: entry.matchedTerms,
    reason: buildReason(industry, entry.intent, entry.matchedTerms),
  }));
}

function buildReason(
  industry: Industry,
  intent: ArticleIntent,
  terms: string[]
): string {
  const sector = industry.shortName.toLowerCase();
  const topic = terms.length ? terms.slice(0, 2).join(" and ") : industry.primaryKeyword;

  switch (intent) {
    case "disputes":
      return `Covers the claim risk ${sector} employers face around ${topic}.`;
    case "pay":
      return `Sets out how ${sector} employers get ${topic} right in payroll terms.`;
    case "people":
      return `Practical for ${sector} teams dealing with ${topic} day to day.`;
    case "change":
      return `Read this before a ${sector} restructure that touches ${topic}.`;
    default:
      return `Explains the process ${sector} employers must follow on ${topic}.`;
  }
}
